import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError } from "axios"
import { fetchUserInfo } from "./dataSlice"
import { AUTH_ENDPOINTS } from "../../config/apiEndpoints"
import { logger } from '../../utils/logger'

interface User {
  id: string
  username: string
  email: string
  role: string
  teams: Team[]
}

interface Team {
  id: string
  name: string
  // Add other team properties as needed
}

interface AuthState {
  authenticate: boolean
  users: User[] | null
  email: string | null
  curUser: User | null
  currTeam: Team | null
  role: string | null
  error: string | null
  verifyError: string | null
  filter: string | null
  ascend: boolean | null
  loading: boolean
  isSubAccount: boolean
  isMasterAccount: boolean
  accountType: 'regular' | 'master' | 'sub'
  parentMasterEmail: string | null
  parentMasterUID: string | null
  /**
   * Indicates whether we've hydrated auth-related state from browser storage.
   * Needed to avoid hydration mismatches on static export / CloudFront refresh.
   */
  hydrated: boolean
}

interface RootState {
  auth: AuthState
}

interface LoginCredentials {
  username: string
  password: string
}

interface RegisterCredentials {
  username: string
  key: string
  email: string
  company: string
  password: string
}

interface VerifyAccountData {
  email: string
  code: string
}


interface ResetPasswordData {
  email: string
  code: string
  newpassword: string
}

/**
 * IMPORTANT:
 * Do NOT read from localStorage in slice initialState.
 * Next.js static export still renders initial HTML in a non-browser environment,
 * so reading browser-only APIs during initial render causes hydration mismatches.
 *
 * We hydrate from localStorage after mount via `hydrateFromStorage`.
 */
const initialState: AuthState = {
  authenticate: false,
  users: null,
  email: null,
  curUser: null,
  currTeam: null,
  role: null,
  error: null,
  verifyError: null,
  filter: null,
  ascend: null,
  loading: false,
  isSubAccount: false,
  isMasterAccount: false,
  accountType: 'regular',
  parentMasterEmail: null,
  parentMasterUID: null,
  hydrated: false,
}


// Regular login (keeps for backward compatibility)
export const login = createAsyncThunk("auth/login", async (authInfo: LoginCredentials, { rejectWithValue }) => {
  const body = { user: authInfo }
  try {
    const response = await axios.post(AUTH_ENDPOINTS.LOGIN, body)
    return response.data
  } catch (err) {
    const error = err as AxiosError
    return rejectWithValue(error.response?.data || "Login failed")
  }
})

// Unified login - tries both regular/master AND sub-account login
export const unifiedLogin = createAsyncThunk(
  "auth/unifiedLogin",
  async (authInfo: LoginCredentials, { rejectWithValue }) => {
    try {
      // First, try regular/master account login
      logger.log("LOG: Attempting regular/master account login");
      
      const body = { user: authInfo };
      const response = await axios.post(AUTH_ENDPOINTS.LOGIN, body);
      
      logger.log("LOG: Regular/master account login successful");
      return { 
        loginType: 'regular', 
        user: response.data.user,
        accountType: response.data.user.isMaster ? 'master' : 'regular'
      };
      
    } catch (regularError) {
      logger.log("LOG: Regular login failed, trying sub-account login");
      logger.log("LOG: Regular login error:", (regularError as AxiosError).response?.data || (regularError as Error).message);
      
      try {
        // Try sub-account login with username and password
        const subLoginCredentials = {
          username: authInfo.username,
          password: authInfo.password
        };
        logger.log("LOG: Attempting sub-account login with username:", subLoginCredentials.username);
        const subResponse = await axios.post(AUTH_ENDPOINTS.SUB_ACCOUNT_LOGIN, subLoginCredentials);
        
        logger.log("LOG: Sub-account login successful");
        return { 
          loginType: 'sub', 
          user: subResponse.data.user,
          accountType: 'sub'
        };
        
      } catch (subError) {
        logger.log("LOG: Sub-account login also failed");
        logger.log("LOG: Sub login error:", (subError as AxiosError).response?.data || (subError as Error).message);
        
        // Both login attempts failed
        return rejectWithValue(
          (subError as AxiosError).response?.data || 
          "Invalid username or password. Please check your credentials."
        );
      }
    }
  }
)

export const logout = createAsyncThunk("auth/logout", async () => {
  localStorage.removeItem("GrainGuardianToken")
  localStorage.removeItem("userEmail")
  localStorage.removeItem("userFilter")
  localStorage.removeItem("userAscend")
  localStorage.removeItem("accountType")
  localStorage.removeItem("isSubAccount")
  return null
})

// Verify authentication token
export const verify = createAsyncThunk(
  "auth/verify",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem("GrainGuardianToken")
    if (!token) {
      return rejectWithValue("No token found")
    }
    try {
      const response = await axios.get(AUTH_ENDPOINTS.VERIFY_TOKEN, {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      return response.data
    } catch (err) {
      const error = err as AxiosError
      return rejectWithValue(error.response?.data || "Token verification failed")
    }
  }
)

// Register new user
export const register = createAsyncThunk(
  "auth/register",
  async (authInfo: RegisterCredentials, { rejectWithValue }) => {
    const body = { user: authInfo }
    try {
      const response = await axios.post(AUTH_ENDPOINTS.REGISTER, body)
      return response.data
    } catch (err) {
      const error = err as AxiosError
      
      // Return more specific error messages
      if (error.response?.status === 400) {
        return rejectWithValue(error.response?.data || "Invalid registration data")
      } else if (error.response?.status === 409) {
        return rejectWithValue("Email already exists")
      } else if (error.response?.status === 401) {
        return rejectWithValue("Invalid registration key")
      } else if (error.response?.status && error.response.status >= 500) {
        return rejectWithValue("Server error. Please try again later.")
      }
      
      return rejectWithValue(error.response?.data || "Registration failed")
    }
  }
)

// Verify account with code
export const verifyAccount = createAsyncThunk(
  "auth/verifyAccount", 
  async (verifyData: VerifyAccountData, { rejectWithValue }) => {
    const body = { user: verifyData }
    try {
      const response = await axios.post(AUTH_ENDPOINTS.VERIFY_ACCOUNT, body)
      return response.data
    } catch (err) {
      const error = err as AxiosError
      return rejectWithValue(error.response?.data || "Verification failed")
    }
  }
)

// Resend verification code
export const resendVerification = createAsyncThunk(
  "auth/resendVerification", 
  async (email: string, { rejectWithValue }) => {
    const body = { user: { email } }
    try {
      const response = await axios.post(AUTH_ENDPOINTS.RESEND_VERIFICATION, body)
      return response.data
    } catch (err) {
      const error = err as AxiosError
      return rejectWithValue(error.response?.data || "Failed to resend verification code")
    }
  }
)

// Forgot password - send reset code
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email: string, { rejectWithValue }) => {
    const body = { user: { email } }
    try {
      const response = await axios.put(AUTH_ENDPOINTS.FORGOT_PASSWORD, body)
      return response.data
    } catch (err) {
      const error = err as AxiosError
      return rejectWithValue(error.response?.data || "Failed to send reset code")
    }
  }
)

// Reset password with verification code
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (resetData: ResetPasswordData, { rejectWithValue }) => {
    // Transform to match backend API format: newpassword (lowercase)
    const body = { 
      user: {
        email: resetData.email,
        code: resetData.code,
        newpassword: resetData.newpassword
      }
    }
    try {
      const response = await axios.post(AUTH_ENDPOINTS.RESET_PASSWORD, body)
      return response.data
    } catch (err) {
      const error = err as AxiosError
      return rejectWithValue(error.response?.data || "Failed to reset password")
    }
  }
)

// Thunk to change filter options (same as mobile app)
export const changeFilterOptions = createAsyncThunk(
  'auth/changeFilterOptions',
  async (payload: { filter: string; ascend: boolean }, { rejectWithValue }) => {
    try {
      const response = await axios.put('/api/users/changeFilterOptions', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('GrainGuardianToken'),
        },
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      return rejectWithValue(axiosError.response?.data || 'Failed to change filter options');
    }
  }
)


export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    hydrateFromStorage: (state) => {
      // NOTE: This reducer must only be dispatched in the browser (useEffect).
      const token = localStorage.getItem("GrainGuardianToken")
      const storedAccountType = localStorage.getItem("accountType") as 'regular' | 'master' | 'sub' | null
      const storedIsSubAccount = localStorage.getItem("isSubAccount") === 'true'

      state.authenticate = !!token
      state.email = localStorage.getItem("userEmail")
      state.filter = localStorage.getItem("userFilter")
      state.ascend =
        localStorage.getItem("userAscend") === "true"
          ? true
          : localStorage.getItem("userAscend") === "false"
            ? false
            : null

      state.accountType = storedAccountType || 'regular'
      state.isSubAccount = storedIsSubAccount
      state.isMasterAccount = storedAccountType === 'master'

      state.hydrated = true
    },
    failAuthenticate: (state) => {
      state.authenticate = false
      state.error = "Authentication failed"
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    clearError: (state) => {
      state.error = null
      state.verifyError = null
    },
    setVerifyError: (state, action) => {
      state.verifyError = action.payload
    },
    setFilterOptions: (state, action) => {
      state.filter = action.payload.filter
      state.ascend = action.payload.ascend
      localStorage.setItem("userFilter", action.payload.filter)
      localStorage.setItem("userAscend", action.payload.ascend.toString())
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        localStorage.setItem("GrainGuardianToken", action.payload.user.token)
        localStorage.setItem("userEmail", action.payload.user.username)
        state.authenticate = true
        state.email = action.payload.user.username
        state.error = null
        state.loading = false
      })
      .addCase(login.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : "Wrong username or password"
        state.loading = false
        state.authenticate = false
      })
      // Unified login cases (handles both regular and sub-account login)
      .addCase(unifiedLogin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(unifiedLogin.fulfilled, (state, action) => {
        logger.log(`LOG: ${action.payload.loginType} login successful`);
        logger.log("LOG: unifiedLogin fulfilled with payload:", action.payload);
        
        // Store token and user info
        localStorage.setItem("GrainGuardianToken", action.payload.user.token)
        localStorage.setItem("userEmail", action.payload.user.username)
        localStorage.setItem("accountType", action.payload.accountType)
        localStorage.setItem("isSubAccount", action.payload.accountType === 'sub' ? 'true' : 'false')
        
        // Update state
        state.authenticate = true
        state.email = action.payload.user.username
        state.error = null
        state.loading = false
        
        // Set account type - CRITICAL for navigation rendering
        state.accountType = action.payload.accountType as 'regular' | 'master' | 'sub';
        state.isSubAccount = action.payload.accountType === 'sub';
        state.isMasterAccount = action.payload.accountType === 'master';
        
        // Set parent info for sub-accounts
        if (action.payload.accountType === 'sub') {
          state.parentMasterEmail = action.payload.user.parentMasterEmail || null;
          state.parentMasterUID = action.payload.user.parentMasterUID || null;
        } else {
          state.parentMasterEmail = null;
          state.parentMasterUID = null;
        }
        
        // Set teams data - CRITICAL for loading SmartProbes
        if (action.payload.user.teams && action.payload.user.teams.length > 0) {
          logger.log("LOG: Setting teams:", action.payload.user.teams);
          // Set currTeam if not already set
          if (state.currTeam === null) {
            state.currTeam = action.payload.user.teams[0];
            logger.log("LOG: Setting currTeam to first team:", state.currTeam);
          }
        }

        logger.log("LOG: Auth state updated - isSubAccount:", state.isSubAccount, "isMasterAccount:", state.isMasterAccount);
      })
      .addCase(unifiedLogin.rejected, (state, action) => {
        logger.log("LOG: unifiedLogin rejected:", action.payload);
        state.error = typeof action.payload === 'string' ? action.payload : "Wrong username or password"
        state.loading = false
        state.authenticate = false
        state.accountType = 'regular'
        state.isSubAccount = false
        state.isMasterAccount = false
        state.parentMasterEmail = null
        state.parentMasterUID = null
      })
      // Logout cases
      .addCase(logout.fulfilled, (state) => {
        state.authenticate = false
        state.email = null
        state.curUser = null
        state.currTeam = null
        state.role = null
        state.error = null
        state.verifyError = null
        state.filter = null
        state.ascend = null
        state.loading = false
        state.accountType = 'regular'
        state.isSubAccount = false
        state.isMasterAccount = false
        state.parentMasterEmail = null
        state.parentMasterUID = null
      })
      // Verify cases
      .addCase(verify.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verify.fulfilled, (state, action) => {
        state.authenticate = true
        state.email = action.payload.user.username
        state.error = null
        state.loading = false
      })
      .addCase(verify.rejected, (state) => {
        state.authenticate = false
        state.loading = false
        // Clear token if verification fails
        localStorage.removeItem("GrainGuardianToken")
      })
      // Register cases
      .addCase(register.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.email = action.payload.user.username
        state.error = null
        state.loading = false
      })
      .addCase(register.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : "Registration failed"
        state.loading = false
      })
      // Verify account cases
      .addCase(verifyAccount.fulfilled, (state) => {
        state.verifyError = null
      })
      .addCase(verifyAccount.rejected, (state) => {
        state.verifyError = "Invalid verification! Please check your email and code again"
      })
      // Resend verification cases
      .addCase(resendVerification.rejected, (state) => {
        state.error = "Failed to send code!"
      })
      // Forgot password cases
      .addCase(forgotPassword.fulfilled, (state) => {
        state.error = null
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : "Failed to send reset code"
      })
      // Reset password cases
      .addCase(resetPassword.fulfilled, (state) => {
        state.error = null
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : "Failed to reset password"
      })
      .addCase(changeFilterOptions.fulfilled, (state, action) => {
        state.filter = action.payload.filter
        state.ascend = action.payload.ascend
        localStorage.setItem("userFilter", action.payload.filter)
        localStorage.setItem("userAscend", action.payload.ascend.toString())
        state.error = null
      })
      .addCase(changeFilterOptions.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : "Failed to change filter options"
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        // Update filter settings from user info if they exist
        if (action.payload.userInfo.filter) {
          state.filter = action.payload.userInfo.filter;
          localStorage.setItem("userFilter", action.payload.userInfo.filter);
        }
        if (action.payload.userInfo.ascend !== null && action.payload.userInfo.ascend !== undefined) {
          state.ascend = action.payload.userInfo.ascend;
          localStorage.setItem("userAscend", action.payload.userInfo.ascend.toString());
        }
      })
  },
})

// Selectors
export const selectIsAuthenticated = (state: RootState) => state.auth.authenticate
export const selectCurrentTeam = (state: RootState) => state.auth.currTeam
export const selectAuthError = (state: RootState) => state.auth.error

export const { hydrateFromStorage, setError, clearError, failAuthenticate, setFilterOptions, setVerifyError } = authSlice.actions
export default authSlice.reducer
